/* @flow */
import idx from 'idx';
import React from 'react';
import gql from 'graphql-tag';
import { Query } from 'react-apollo';
import CONTENT_SOURCE_V1_QUERY from 'user/queries/contentSources.gql';
import CONTENT_SOURCE_V2_QUERY from 'user/queries/contentSourcesV2.gql';
import type { ContentSource } from '../components/AccountLinkingComposerSidebarServices.jsx';

type Props = {
  fetchPolicy?: string,
  children: ({
    loading: boolean,
    error?: Error,
    sources: ?Array<ContentSource>,
    refetch: Function,
  }) => any,
};

const V2ToContentSource = (v2: *): ?Array<ContentSource> => {
  if (!v2) {
    return null;
  }

  return v2.map(source => {
    return {
      title: source.title,
      providerKey: source.providerKey,
      iconURL: source.iconURL,
      linkedAccountInfo: source.linkedAccountInfo
        ? {
            authUrl: source.linkedAccountInfo.authUrl,
            isLinked: source.linkedAccountInfo.isLinked,
            providerKey: source.linkedAccountInfo.providerKey,
          }
        : undefined,
    };
  });
};

const V1ToContentSource = (v1: *): ?Array<ContentSource> => {
  if (!v1) {
    return null;
  }

  return v1.map(source => {
    return {
      title: source.name,
      providerKey: source.source,
      iconURL: source.imageUrl,
      linkedAccountInfo: source.linkedAccount
        ? {
            authUrl: source.linkedAccount.authUrl,
            isLinked: source.linkedAccount.isLinked,
            providerKey: source.linkedAccount.provider,
          }
        : undefined,
    };
  });
};

const ContainerV1 = (props: Props) => (
  <Query query={CONTENT_SOURCE_V1_QUERY} fetchPolicy={props.fetchPolicy}>
    {({ loading, error, data, refetch }) =>
      props.children({
        error,
        loading,
        sources: V1ToContentSource(idx(data, _ => _.me.composer.contentSources)),
        refetch,
      })
    }
  </Query>
);

const ContainerV2 = (props: Props) => (
  <Query query={CONTENT_SOURCE_V2_QUERY} fetchPolicy={props.fetchPolicy}>
    {({ loading, error, data, refetch }) =>
      props.children({
        error,
        loading,
        sources: V2ToContentSource(idx(data, _ => _.me.composer.contentSourcesInfinity)),
        refetch,
      })
    }
  </Query>
);

export const QUERY = gql`
  query AccountLinkingComposerSidebarQuery($feature: String!, $experiment: String!) {
    me {
      uuid
      feature: has_feature(name: $feature)
      experiments(names: [$experiment]) {
        variation
      }
    }
  }
`;

function hasFeature(data, value) {
  return idx(data, _ => _.me.feature) === value;
}

function hasExperiment(data, variation) {
  return idx(data, _ => _.me.experiments[0].variation) === variation;
}

const FEATURE = '2018.developer.whitelist';
const EXPERIMENT = '2018.apr.web.content_source_v2';

/* This container fetches the content sources.
 * We currently have an experiment with two queries.
 * We cast each query results into an common type of ContentSource.
 * This type is defined in the UI component: AccountLinkingComposerSidebarServices
 */
const AccountLinkingComposerSidebarServicesContainer = (props: Props) => (
  <Query query={QUERY} variables={{ feature: FEATURE, experiment: EXPERIMENT }}>
    {({ loading, data }) => {
      if (loading) {
        return null;
      }

      return hasFeature(data, true) || hasExperiment(data, 'variant') ? (
        <ContainerV2 {...props} />
      ) : (
        <ContainerV1 {...props} />
      );
    }}
  </Query>
);

export default AccountLinkingComposerSidebarServicesContainer;
